// DEV-89 (`oq-ММБ-121` (а)): возврат на исходную страницу после входа.
//
// Когда продление сессии не удалось, `http/index.ts` уводит человека на страницу
// входа, и исходный адрес терялся: после входа он попадал на страницу по роли, а
// не туда, где работал (например, `/plants?plantId=7&tab=list`). Здесь этот адрес
// запоминается и отдаётся обратно.
//
// Принимается **только внутренний путь этого приложения**. Внешний адрес или
// `//host` — это открытое перенаправление: после входа человека увели бы на
// чужой сайт. Такие значения отбрасываются, и берётся страница по роли.

export const RETURN_PATH_KEY = "authReturnPath";

/** Куда входит роль, если возвращаться некуда. */
export const LANDING_BY_ROLE: Record<string, string> = {
  root: "/organizations",
  "оператор организации": "/plants",
  "оператор объекта": "/inplants",
  // `Ф0` (`АРХ-ЛК-20.10`): кабинет — общий для ПЗС и владельца станции.
  "ПЗС": "/cabinet/stations",
  "владелец станции": "/cabinet/stations",
};

/** Страница по умолчанию для роли. */
export function landingForRole(role?: string | null): string {
  return LANDING_BY_ROLE[String(role)] || "/";
}

/**
 * Внутренний ли это путь. Отбрасываются:
 * - не строка и пустое значение;
 * - `//host` и `/\host` — протокол-относительный адрес, браузер читает их как
 *   чужой сайт (`\` браузеры приводят к `/`);
 * - внешний адрес (`http://…`, `https://…`, `javascript:…`) — он не начинается
 *   с одного `/`;
 * - «/» — сама страница входа (`DEV-81`): возвращаться на неё незачем.
 */
export function isInternalPath(value: unknown): boolean {
  if (typeof value !== "string") return false;

  const path = value.trim();

  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("\\")) return false;
  if (/^\/+$/.test(path)) return false;

  return true;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    // Приватный режим и подобное: возврат просто не сработает — вход не ломается.
    return null;
  }
}

function currentLocation(): string | null {
  if (typeof window === "undefined" || !window.location) return null;

  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Запомнить исходный адрес — путь вместе со строкой запроса.
 *
 * Хранилище — `sessionStorage`: событие «сессия истекла» относится к этой вкладке,
 * и адрес не должен переезжать в соседнюю вкладку или жить после её закрытия.
 */
export function rememberReturnPath(path: unknown): void {
  const store = storage();
  if (!store) return;

  if (!isInternalPath(path)) {
    store.removeItem(RETURN_PATH_KEY);
    return;
  }

  store.setItem(RETURN_PATH_KEY, String(path).trim());
}

/** Забыть запомненный адрес. */
export function forgetReturnPath(): void {
  storage()?.removeItem(RETURN_PATH_KEY);
}

/**
 * Исходный адрес для возврата или `null`.
 *
 * Значение **не «съедается» при чтении**: его читают и обработчик входа, и
 * страница входа, и оба должны получить один и тот же адрес — иначе второй увёл
 * бы человека на страницу по роли. Забывается оно тогда, когда человек на этот
 * адрес уже попал (`location` совпала с запомненной).
 *
 * @param location — где человек сейчас (по умолчанию — адрес страницы).
 */
export function peekReturnPath(location?: string): string | null {
  const store = storage();
  if (!store) return null;

  const stored = store.getItem(RETURN_PATH_KEY);
  if (!stored || !isInternalPath(stored)) return null;

  const current = location === undefined ? currentLocation() : location;

  if (current !== null && current === stored) {
    // Вернулись — больше адрес не нужен: иначе он увёл бы сюда следующий вход.
    store.removeItem(RETURN_PATH_KEY);
    return null;
  }

  return stored;
}

/** Куда вести после входа: исходный адрес, если он есть, иначе страница роли. */
export function postLoginTarget(
  role?: string | null,
  location?: string,
): string {
  return peekReturnPath(location) || landingForRole(role);
}
